import { Fragment, type ReactNode } from 'react';
import { INNER_CELL, INNER_TABLE } from './TableCard';
import type { EntrySide, TableHole } from './hardwareInteractionsRun';
import { namedIn, withLetter } from './partsCatalogue';
import { sidedName, symmetryOf, type PartItem } from './partsRun';
import { makesOwnHole, shownHoleId } from './stepHoles';
import { useLanguage } from '../../contexts/LanguageContext';

// THE JOINTS TABLE'S HOLES, ONE ROW PER PIECE OF HARDWARE'S LINE — the Joints table's cell, and the
// Drilling table with its two figures after.
//
// CONNECTED HOLES ARE ONE ROW: the screw's through hole in the frame and its blind hole in the rail
// (TableHole.aligned, either way round) are one line drilled for one screw — each cell a line per
// hole, in the order the hardware goes through them. A cross hole is its own row, said as the hole
// it crosses: another piece of hardware, another diameter, another axis.

// AN ID TWO HOLES CLAIM, over every line given — said in the warning colour where it is shown.
export const twiceIdsOf = (holes: TableHole[]) => {
  const all = holes.flatMap((h) => h.ids ?? []);
  return new Set(all.filter((id, i) => all.indexOf(id) !== i));
};

const drilled = (h: TableHole) => h.kind === 'through' || h.kind === 'blind';
const joined = (a: TableHole, b: TableHole) =>
  a.for === b.for && (a.aligned === b.part || b.aligned === a.part);

// ONE ROW: its holes in order, the k-th of each line, the entry's side and all its holes (what a
// cross hole meets is found among them), and the row's ids.
// `washers`: the washers on the row's hardware, by id — shown with it.
// `crossing`: THE CROSS HOLE DRILLED ACROSS THIS LINE, its k-th — the barrel nut's across the screw's:
// the two work together, so one row, an "×" between them.
export type HoleRow = {
  holes: TableHole[]; k: number; side: EntrySide; all: TableHole[]; ids: (string | undefined)[]; washers: string[];
  crossing?: { h: TableHole; id?: string };
};

// AN ENTRY'S LINES, CONNECTED: each drilled hole with every one the same hardware runs on into,
// through holes first (where the hardware goes in); each cross hole alone.
function linesOf(holes: TableHole[]): TableHole[][] {
  const left = holes.filter(drilled);
  const out: TableHole[][] = [];
  while (left.length) {
    const line = [left.shift()!];
    for (let grew = true; grew;) {
      grew = false;
      for (let i = left.length - 1; i >= 0; i -= 1) {
        if (line.some((h) => joined(h, left[i]))) { line.push(...left.splice(i, 1)); grew = true; }
      }
    }
    out.push(line.sort((a, b) => Number(b.kind === 'through') - Number(a.kind === 'through')));
  }
  return [...out, ...holes.filter((h) => h.kind === 'cross').map((h) => [h])];
}

// THE CROSS HOLES ACROSS ONE LINE: those for the hardware crossing it, in one of its parts — one per
// piece on the line, or none at all: counts that disagree leave each on a row of its own, to be seen.
const crossingsOf = (line: TableHole[], holes: TableHole[]): TableHole[] => {
  const n = Math.max(...line.map((h) => h.count));
  const across = holes.filter((c) => c.kind === 'cross' && c.crosses === line[0].for
    && line.some((h) => h.part === c.part));
  return across.reduce((a, c) => a + c.count, 0) === n ? across : [];
};

// EVERY LINE OF THE ENTRIES GIVEN, each piece of hardware on it a row, in the order of their ids — a
// cross hole across it in the same row. A part met without a hole (a felt pad's foot) drills nothing,
// and is no row.
export const holeRows = (
  entries: { holes: TableHole[]; side: EntrySide; washers?: Record<string, string> }[],
): HoleRow[] =>
  entries.flatMap((entry) => {
    const lines = linesOf(entry.holes);
    const merged = new Set<TableHole>();
    const rowsOf = (line: TableHole[]) => {
      const across = line[0].kind === 'cross' ? [] : crossingsOf(line, entry.holes);
      across.forEach((c) => merged.add(c));
      // The k-th piece's cross hole: the cross lines' holes one after another.
      const pieces = across.flatMap((c) => Array.from({ length: c.count }, (_, j) => ({ h: c, id: c.ids?.[j] })));
      return Array.from({ length: Math.max(1, ...line.map((h) => h.count)) }, (_, k): HoleRow => ({
        holes: line, k, side: entry.side, all: entry.holes, ids: line.map((h) => h.ids?.[k]),
        washers: Object.entries(entry.washers ?? {}).filter(([, on]) => on === line[0].for).map(([w]) => w),
        crossing: pieces[k],
      }));
    };
    const drilledRows = lines.filter((l) => l[0].kind !== 'cross').flatMap(rowsOf);
    return [...drilledRows, ...lines.filter((l) => l[0].kind === 'cross' && !merged.has(l[0])).flatMap(rowsOf)];
  })
    .sort((a, b) => (a.ids.find(Boolean) ?? '').localeCompare(b.ids.find(Boolean) ?? '', undefined, { numeric: true }));

// A ROW'S HOLES, a line each — the line's, an arrow between two, then the one crossing it after an
// "×" — so every per-hole column lines up with the Ids.
export const holesOf = (r: HoleRow) => [
  ...r.holes.map((h, i) => ({ h, id: r.ids[i], sep: i ? '→' : '' })),
  ...(r.crossing ? [{ h: r.crossing.h, id: r.crossing.id, sep: '×' }] : []),
];
export const perHole = (r: HoleRow, draw: (h: TableHole, id: string | undefined, crossing: boolean) => ReactNode) =>
  holesOf(r).map(({ h, id, sep }, i) => (
    <Fragment key={i}>
      {sep && <div>{sep}</div>}
      <div>{draw(h, id, sep === '×')}</div>
    </Fragment>
  ));

// A ROW'S PIECES OF HARDWARE, a cell's lines each: the one on its line, then — an "×" between — the
// one crossing it. What the Hardware cell and the Drilling table's figures are drawn by.
export const piecesOf = (r: HoleRow) => [r.holes[0].for, ...(r.crossing ? [r.crossing.h.for] : [])];
export const byPiece = (r: HoleRow, draw: (hw: string) => ReactNode) => piecesOf(r).map((hw, i) => (
  <Fragment key={hw}>
    {i > 0 && <div>×</div>}
    <div>{draw(hw)}</div>
  </Fragment>
));

// A PIECE OF HARDWARE WITH THE WASHERS ADDED TO IT, each in brackets on a line of its own under it:
// the piece is what goes in, the washer comes with it — "JCBC screw (E)" / "(+ Spring washer (F))".
export const withWashers = (name: string, washers: string[]) => (
  <>
    <div>{name}</div>
    {washers.map((w) => <div key={w}>{`(+ ${w})`}</div>)}
  </>
);

// HOW A HOLES TABLE NAMES THINGS: a face lowercase in the reader's language ('' for none), a part
// by its name and — one of a mirror pair — its side, and the hardware with its letter.
function useHoleWords(items: PartItem[]) {
  const { t, language } = useLanguage();
  return {
    faceWord: (face: string) =>
      (face ? t(`notice.assembly.face${face[0].toUpperCase()}${face.slice(1)}`).toLowerCase() : ''),
    partName: (id: string, side: EntrySide) => sidedName(
      namedIn(items[Number(id)], language) || `#${id}`,
      symmetryOf(items[Number(id)]) === 'left-right' ? side : null,
      language,
    ),
    hardwareName: (id: string) => (items[Number(id)]
      ? withLetter(namedIn(items[Number(id)], language) || `#${id}`, items[Number(id)].letter) : `#${id}`),
  };
}

// `cols`: every column's width, where the caller has measured them (see HardwareTable) — its
// tables under each other line up. `extra`: columns after, their heads and each row's cells.
// `onRow` / `isOn`: a row as a switch, drawn on where it is on. `partLink`: a part's name drawn as
// whatever the caller makes of it — a link to the part's own view.
export function HolesGrid({ rows, items, twiceIds, cols, extra, onRow, isOn, partLink }: {
  rows: HoleRow[];
  items: PartItem[];
  twiceIds: Set<string>;
  cols?: number[] | null;
  extra?: { heads: string[]; cells: (r: HoleRow) => ReactNode };
  onRow?: (r: HoleRow) => void;
  isOn?: (r: HoleRow) => boolean;
  partLink?: (part: string, label: string) => ReactNode;
}) {
  const { t } = useLanguage();
  const { faceWord, partName, hardwareName } = useHoleWords(items);
  // WHAT A CROSS HOLE IS: "crosses H103", the k-th hole of the line it crosses in its own part. In
  // the warning colour where it names no hole (an answer from before it was asked, or a link the
  // server could not find), and where the two counts differ: a barrel nut per screw means as many
  // crossings as holes crossed.
  const kindOf = (h: TableHole, r: HoleRow) => {
    if (h.kind !== 'cross') return <>{t(`notice.hardwareTable.hole.${h.kind}`)}</>;
    const met = r.all.filter((o) => o.part === h.part && o.for === h.crosses && o.kind !== 'cross');
    const id = met.flatMap((o) => o.ids ?? [])[r.k];
    if (!h.crosses || !id) {
      return (
        <span className="text-amber-600" title={t('notice.hardwareTable.crossUnlinkedTip')}>
          {t('notice.hardwareTable.hole.cross')}
        </span>
      );
    }
    const agree = met.reduce((n, o) => n + o.count, 0) === h.count;
    return <span className={agree ? '' : 'text-amber-600'}>{t('notice.drilling.crosses').replace('{id}', id)}</span>;
  };
  const head = (label: string) => <th key={label} className={`${INNER_CELL} font-semibold`}>{label}</th>;

  return (
    // FIXED at the measured widths, and as wide as their sum, where they were given.
    <table
      data-holes
      className={`${INNER_TABLE} tabular-nums`}
      style={cols ? { tableLayout: 'fixed', width: cols.reduce((a, w) => a + w, 0) } : undefined}
    >
      {cols && <colgroup>{cols.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>}
      <thead>
        <tr>
          {head(t('notice.hardwareTable.holeIds'))}
          {head(t('notice.drilling.parts'))}
          {head(t('notice.hardwareTable.holeHardware'))}
          {extra?.heads.map(head)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const hw = r.holes[0].for;
          const on = !!isOn?.(r);
          // A POINTED SCREW'S LINE, said (Louis 2026-10-29): where it goes in, nothing drilled for it
          // (see makesOwnHole).
          const ownHole = makesOwnHole(items[Number(hw)]);
          return (
            <tr
              key={`${r.ids.join('+')}-${hw}-${r.side}-${r.k}-${r.holes.map((h) => h.part).join(',')}`}
              onClick={onRow && (() => onRow(r))}
              aria-pressed={onRow ? on : undefined}
              className={onRow ? `cursor-pointer transition-colors ${on ? 'bg-blue-50' : 'hover:bg-slate-50'}` : undefined}
            >
              {/* THE CALL'S IDS, as stored — in the warning colour where a line has not one per hole,
                  or another line has taken this one. */}
              <td data-col={0} className={INNER_CELL}>
                {perHole(r, (h, id) => {
                  const off = !!h.ids && (h.ids.length !== h.count || (!!id && twiceIds.has(id)));
                  return <span className={off ? 'text-amber-600' : ''}>{id ? shownHoleId(id, ownHole) : ''}</span>;
                })}
              </td>
              {/* THE PART, ITS FACE AND THE KIND OF HOLE, then the parts the hardware goes on into —
                  an arrow on a line of its own between two, in both columns, so they stay level. */}
              <td data-col={1} className={INNER_CELL}>
                {perHole(r, (h, _id, crossing) => {
                  const name = partName(h.part, r.side);
                  return (
                    <>
                      {partLink ? partLink(h.part, name) : name}
                      {` (${faceWord(h.face)}, `}{crossing ? t('notice.hardwareTable.hole.cross') : kindOf(h, r)})
                    </>
                  );
                })}
              </td>
              {/* THE HARDWARE, and the washer added to it — "JCBC screw (E) (+ Spring washer (F))" — then
                  the hardware crossing it. */}
              <td data-col={2} className={INNER_CELL}>
                {byPiece(r, (one) => (one === hw ? withWashers(hardwareName(hw), r.washers.map(hardwareName)) : hardwareName(one)))}
                {ownHole && <div className="text-[10px] italic text-slate-500">{t('notice.holes.ownHole')}</div>}
              </td>
              {extra?.cells(r)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
